/**
 * AMOXTLI - Main Application Layout
 * 
 * Propiedad Intelectual: Soluciones TecnoExpertas SAPI de CV
 * Autor: Luis Alberto Orozco Arias
 * Licencia: Propietaria - Todos los derechos reservados © 2026
 * 
 * Descripción: Layout principal con navegación, sidebar y footer
 */

import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'

import { useStore } from '../services/store'
import { useTheme } from '../services/ThemeContext'
import { OnboardingGuide } from './OnboardingGuide'
import AmoxtliIcon from './AmoxtliIcon'
import AIHelpButton from './AIHelpButton'

export function Layout({ children }) {
  const navigate = useNavigate()
  const logout = useStore((state) => state.logout)
  const user = useStore((state) => state.user)
  const { theme } = useTheme()
  const [menuAbierto, setMenuAbierto] = useState(false)
  const [menuSecundarioAbierto, setMenuSecundarioAbierto] = useState(false)
  const menuSecundarioRef = useRef(null)

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    logout()
    navigate('/login')
  }
  
  const isAdmin = user?.rol === 'admin'
  const isSupervisor = user?.rol === 'supervisor'
  const tabs = new Set(user?.tabs_permitidas || [])
  const can = (tab) => isAdmin || isSupervisor || tabs.has(tab)

  useEffect(() => {
    if (!menuSecundarioAbierto) {
      return undefined
    }

    const handleClickOutside = (event) => {
      if (menuSecundarioRef.current && !menuSecundarioRef.current.contains(event.target)) {
        setMenuSecundarioAbierto(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [menuSecundarioAbierto])

  const navConfig = [
    { to: '/dashboard', label: 'Inicio', show: true, group: 'principal' },
    { to: '/empresas', label: 'Empresas', show: can('configuracion'), group: 'principal' },
    { to: '/clientes', label: 'Clientes', show: can('clientes'), group: 'principal' },
    { to: '/facturas', label: 'Facturas', show: can('facturas'), group: 'principal' },
    { to: '/declaraciones', label: 'Declaraciones', show: can('declaraciones'), group: 'principal' },
    { to: '/cobros', label: 'Suscripción y cobros', show: can('pagos') || can('suscripciones') || can('licencias') || isAdmin || isSupervisor, group: 'principal' },

    { to: '/facturas-descarga', label: 'Descarga SAT', show: can('facturas'), group: 'operacion' },
    { to: '/proveedores', label: 'Proveedores', show: can('proveedores'), group: 'operacion' },
    { to: '/reportes', label: 'Reportes', show: can('reportes'), group: 'operacion' },
    { to: '/plantillas-pdf', label: 'Plantillas PDF', show: can('configuracion'), group: 'gestion' },
    { to: '/admin/usuarios', label: 'Usuarios', show: isAdmin || isSupervisor || can('admin_usuarios'), group: 'gestion' },
    { to: '/mi-cuenta', label: 'Mi cuenta', show: true, group: 'cuenta' },
    { to: '/ayuda', label: 'Ayuda', show: true, group: 'cuenta' },
  ].filter((link) => link.show)

  const primaryNavLinks = navConfig.filter((link) => link.group === 'principal')
  const secondaryNavGroups = [
    {
      title: 'Operación adicional',
      links: navConfig.filter((link) => link.group === 'operacion'),
    },
    {
      title: 'Gestión',
      links: navConfig.filter((link) => link.group === 'gestion'),
    },
    {
      title: 'Cuenta y ayuda',
      links: navConfig.filter((link) => link.group === 'cuenta'),
    },
  ].filter((group) => group.links.length > 0)

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.colors.background}`}>
      {/* Guía interactiva de primera sesión */}
      <OnboardingGuide />
      
      {/* Botón de asistente de IA */}
      <AIHelpButton />

      {/* Header */}
      <header className={`${theme.tailwind.header} shadow-sm border-b`} style={{ borderColor: theme.colors.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className={`text-2xl font-bold ${theme.tailwind.title} flex items-center gap-2`}>
              <AmoxtliIcon variant="book" className="h-7 w-7" alt="Logo Amoxtli" />
              <span>AMOXTLI</span>
            </h1>

            {/* Nav desktop */}
            <nav className="hidden md:flex flex-wrap gap-3 items-center">
              {primaryNavLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`${theme.tailwind.navLink} text-sm whitespace-nowrap`}
                >
                  {link.label}
                </Link>
              ))}
              {secondaryNavGroups.length > 0 && (
                <div className="relative" ref={menuSecundarioRef}>
                  <button
                    type="button"
                    onClick={() => setMenuSecundarioAbierto((prev) => !prev)}
                    className={`${theme.tailwind.navLink} inline-flex items-center gap-1 text-sm whitespace-nowrap`}
                    aria-expanded={menuSecundarioAbierto}
                    aria-haspopup="true"
                  >
                    Más
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  {menuSecundarioAbierto && (
                    <div
                      className="absolute right-0 z-30 mt-2 w-72 rounded-2xl border bg-white p-3 shadow-xl"
                      style={{ borderColor: theme.colors.border }}
                    >
                      <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                        Accesos secundarios
                      </p>
                      <div className="space-y-3">
                        {secondaryNavGroups.map((group) => (
                          <div key={group.title}>
                            <p className="px-2 pb-1 text-xs font-semibold text-slate-500">{group.title}</p>
                            <div className="space-y-1">
                              {group.links.map((link) => (
                                <Link
                                  key={link.to}
                                  to={link.to}
                                  className="block rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50 hover:text-slate-900"
                                  onClick={() => setMenuSecundarioAbierto(false)}
                                >
                                  {link.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={handleLogout}
                className={`${theme.tailwind.navLink} text-sm`}
              >
                Salir
              </button>
            </nav>

            {/* Hamburguesa mobile */}
            <button
              type="button"
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              onClick={() => setMenuAbierto(prev => !prev)}
              aria-label="Menú"
            >
              {menuAbierto ? (
                /* X */
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                /* Hamburguesa */
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Menú mobile desplegable */}
          {menuAbierto && (
            <nav className={`md:hidden mt-3 pb-2 border-t pt-3 flex flex-col gap-1`} style={{ borderColor: theme.colors.border }}>
              <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                Uso diario
              </p>
              {primaryNavLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block px-3 py-2 rounded-lg text-sm ${theme.tailwind.navLink}`}
                  onClick={() => setMenuAbierto(false)}
                >
                  {link.label}
                </Link>
              ))}
              {secondaryNavGroups.map((group) => (
                <div key={group.title} className="pt-3">
                  <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {group.title}
                  </p>
                  {group.links.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`block px-3 py-2 rounded-lg text-sm ${theme.tailwind.navLink}`}
                      onClick={() => setMenuAbierto(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              ))}
              <button
                type="button"
                onClick={() => { setMenuAbierto(false); handleLogout() }}
                className="text-left px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50"
              >
                Salir
              </button>
            </nav>
          )}
        </div>
      </header>
      
      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  )
}

export default Layout
